import { useContext, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useDatasetConfig, useDatasetAtoms } from '../dataset-context';
import type { FilterFieldConfig } from '../types';
import { FilterFieldScope, useIsFilterRail } from './context';

/**
 * The facets a filter template renders against: the live modal-scoped counts
 * when present, otherwise the last committed search's facets.
 * @return {DatasetFacets | undefined} The facets.
 */
export function useModalFacets() {
  const atoms = useDatasetAtoms();
  const modalFacets = useAtomValue(atoms.modalFacetsAtom);
  const committedFacets = useAtomValue(atoms.committedFacetsAtom);
  return modalFacets ?? committedFacets ?? undefined;
}

/**
 * The committed wells search's potential-duplicate count (wells with
 * duplicate_count > 0), or null for datasets without it. Used by the Potential
 * Duplicates checkbox to show a count beside its label.
 * @return {number | null} The count, or null.
 */
export function useModalDuplicateCount() {
  const atoms = useDatasetAtoms();
  return useAtomValue(atoms.committedDuplicateCountAtom);
}

/**
 * The config fields in a category narrowed to a single filter type — the shape
 * every template needs — or to the one field the form has scoped it to.
 * Centralizes the predicate + type guard.
 * @param {string} category The filter category.
 * @param {string} type The filter type.
 * @return {FilterFieldConfig[]} The fields.
 */
export function useCategoryFields<T extends FilterFieldConfig['type']>(
  category: string,
  type: T
): Extract<FilterFieldConfig, { type: T }>[] {
  const config = useDatasetConfig();
  const scopedId = useContext(FilterFieldScope);
  return config.filters.filter(
    (f): f is Extract<FilterFieldConfig, { type: T }> =>
      f.category === category &&
      f.type === type &&
      (scopedId === undefined || f.id === scopedId)
  );
}

/**
 * Whether a facet filter is left out in the rail, which hides empty ones: only
 * while it has offered no options and has nothing chosen. Once the entity's
 * options have shown, the filter stays even when other filters narrow its
 * options to none, so applying one filter never takes another away.
 * @param {boolean} hasOptions The filter has options now.
 * @param {boolean} hasValue The filter has something chosen or typed.
 * @return {boolean} Whether to leave the filter out.
 */
export function useHiddenWhileEmpty(
  hasOptions: boolean,
  hasValue: boolean
): boolean {
  const rail = useIsFilterRail();
  const [offered, setOffered] = useState(hasOptions);
  if (hasOptions && !offered) setOffered(true);
  return rail && !offered && !hasOptions && !hasValue;
}
